import React, { useRef, useState } from "react";

export default function Contact() {
  const sourceLanguageOptions = ["Arabic", "French", "Spanish"];
  const fixedTargetLanguage = "English";

  const [formData, setFormData] = useState({
    fullName: "",
    emailAddress: "",
    sourceLanguage: "",
    targetLanguage: fixedTargetLanguage,
    translationType: "Certified",
    deadline: "Standard",
    message: "",
  });

  const [isDraggingFiles, setIsDraggingFiles] = useState(false);
  const [isDraggingNamesFile, setIsDraggingNamesFile] = useState(false);
  const [documentFiles, setDocumentFiles] = useState([]);
  const [namesFiles, setNamesFiles] = useState([]);

  const [statusMessage, setStatusMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const fileInputRef = useRef(null);
  const namesFileInputRef = useRef(null);

  const usesArabic =
    formData.sourceLanguage === "Arabic" || formData.targetLanguage === "Arabic";

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "documentFiles") {
      handleDocumentFilesSelected(files);
      return;
    }

    if (name === "namesFile") {
      handleNamesFileSelected(files);
      return;
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      targetLanguage: fixedTargetLanguage,
    }));
  };

  const handleRemoveFile = (indexToRemove) => {
    setStatusMessage("");
    setIsError(false);

    const updatedFiles = documentFiles.filter((_, index) => index !== indexToRemove);
    setDocumentFiles(updatedFiles);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveNamesFile = (indexToRemove) => {
    setStatusMessage("");
    setIsError(false);

    setNamesFiles((currentFiles) =>  
      currentFiles.filter((_, index) => index !== indexToRemove)
    );

    if (namesFileInputRef.current) {
      namesFileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      emailAddress: "",
      sourceLanguage: "",
      targetLanguage: fixedTargetLanguage,
      translationType: "Certified",
      deadline: "Standard",
      message: "",
    });

    setDocumentFiles([]);
    setNamesFiles([]);
    setIsDraggingFiles(false);
    setIsDraggingNamesFile(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }

    if (namesFileInputRef.current) {
      namesFileInputRef.current.value = "";
    }
  };

  const MAX_FILES = 10;
  const MAX_FILE_SIZE = 10 * 1024 * 1024;

  const allowedExtensions = [".pdf", ".jpg", ".jpeg", ".png", ".docx"];

  const getFileExtension = (fileName = "") => {
    const lastDot = fileName.lastIndexOf(".");
    return lastDot === -1 ? "" : fileName.slice(lastDot).toLowerCase();
  };

  const validateSelectedFiles = (files) => {
    const fileList = Array.from(files || []);

    if (fileList.length > MAX_FILES) {
      alert("You can upload a maximum of 10 files.");
      return [];
    }

    const validFiles = [];

    for (const file of fileList) {
      const extension = getFileExtension(file.name);

      if (!allowedExtensions.includes(extension)) {
        alert(`File type not allowed: ${file.name}`);
        continue;
      }

      if (file.size > MAX_FILE_SIZE) {
        alert(`File is too large: ${file.name}. Maximum size is 10 MB.`);
        continue;
      }

      validFiles.push(file);
    }

    return validFiles;
  };

const mergeUniqueFiles = (currentFiles, newFiles) => {
  const mergedFiles = [...currentFiles];

  for (const file of newFiles) {
    const alreadyExists = mergedFiles.some(
      (existingFile) =>
        existingFile.name === file.name &&
        existingFile.size === file.size &&
        existingFile.lastModified === file.lastModified
    );

    if (!alreadyExists) {
      mergedFiles.push(file);
    }
  }

  return mergedFiles.slice(0, MAX_FILES);
};

const handleDocumentFilesSelected = (files) => {
  setStatusMessage("");
  setIsError(false);

  const validFiles = validateSelectedFiles(files);

  setDocumentFiles((currentFiles) => {
    const mergedFiles = mergeUniqueFiles(currentFiles, validFiles);

    if (currentFiles.length + validFiles.length > MAX_FILES) {
      setIsError(true);
      setStatusMessage("You can upload a maximum of 10 document files only.");
    }

    return mergedFiles;
  });
};

const handleNamesFileSelected = (files) => {
  setStatusMessage("");
  setIsError(false);

  const validFiles = validateSelectedFiles(files);

  setNamesFiles((currentFiles) => {
    const mergedFiles = mergeUniqueFiles(currentFiles, validFiles);

    if (currentFiles.length + validFiles.length > MAX_FILES) {
      setIsError(true);
      setStatusMessage("You can upload a maximum of 10 names-list files only.");
    }

    return mergedFiles;
  });
};

  const handleDocumentsDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFiles(false);

    handleDocumentFilesSelected(event.dataTransfer.files);
  };

  const handleDocumentsDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFiles(true);
  };

  const handleDocumentsDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingFiles(false);
  };

  const handleNamesDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingNamesFile(false);

    handleNamesFileSelected(event.dataTransfer.files);
  };

  const handleNamesDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingNamesFile(true);
  };

  const handleNamesDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDraggingNamesFile(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setIsSending(true);
    setStatusMessage("");
    setIsError(false);

    if (formData.sourceLanguage === formData.targetLanguage) {
      setIsError(true);
      setStatusMessage("Source language and target language must be different.");
      setIsSending(false);
      return;
    }

    try {
      const formPayload = new FormData();

      formPayload.append("fullName", formData.fullName);
      formPayload.append("full_name", formData.fullName);

      formPayload.append("emailAddress", formData.emailAddress);
      formPayload.append("email", formData.emailAddress);

      // Document type removed from visible form.
      // Default value is sent only for backend/database compatibility.
      formPayload.append("documentType", "Translation Document");
      formPayload.append("document_type", "Translation Document");

      formPayload.append("sourceLanguage", formData.sourceLanguage);
      formPayload.append("source_language", formData.sourceLanguage);

      formPayload.append("targetLanguage", formData.targetLanguage);
      formPayload.append("target_language", formData.targetLanguage);

      // Pages removed from visible form.
      formPayload.append("pages", "1");
      formPayload.append("pageCount", "1");
      formPayload.append("page_count", "1");

      formPayload.append("translationType", formData.translationType);
      formPayload.append("translation_type", formData.translationType);

      formPayload.append("deadline", formData.deadline);

      // Country of use removed.
      formPayload.append("countryOfUse", "");
      formPayload.append("country_of_use", "");

      formPayload.append("message", formData.message);

      documentFiles.forEach((file) => {
        formPayload.append("documentFiles", file);
      });

      if (usesArabic) {
        namesFiles.forEach((file) => {
          formPayload.append("namesFile", file);
        });
      }

      const response = await fetch(
        "https://api.mykcertifiedtranslations.com/api/quote-requests",
        {
          method: "POST",
          body: formPayload,
        }
      );

      const responseText = await response.text();

      let data = {};

      try {
        data = responseText ? JSON.parse(responseText) : {};
      } catch {
        throw new Error(
          `The server returned an invalid response. HTTP ${response.status}`
        );
      }

      if (!response.ok || !data.success) {
        throw new Error(
          data.message || `Failed to submit quote request. HTTP ${response.status}`
        );
      }
      setIsError(false);
      setStatusMessage("Quote request submitted successfully.");

      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead", {
          form_name: "translation_quote",
        });
      }

      resetForm();
    } catch (error) {
      console.error("Quote submission error:", error);

      setIsError(true);
      setStatusMessage(
        error.message || "Failed to submit quote request."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <section className="section" id="quote">
      <div className="container">
        <div className="contact-box">
          <div className="contact-text">
            <span className="section-label">Get in Touch</span>

            <h2>Request a Translation Quote</h2>

            <p>
              Send your translation details and attach your documents for review.
            </p>

            <div className="contact-points">
              <div className="contact-point">✓ Fast response</div>
              <div className="contact-point">✓ Clear communication</div>
              <div className="contact-point">✓ Professional service</div>
              <div className="contact-point">✓ Multiple file upload supported</div>
            </div>
          </div>

          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-field">
              <label htmlFor="fullName">Full Name</label>
              <input
                id="fullName"
                type="text"
                name="fullName"
                placeholder="Full Name"
                value={formData.fullName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-field">
              <label htmlFor="emailAddress">Email Address</label>
              <input
                id="emailAddress"
                type="email"
                name="emailAddress"
                placeholder="Email Address"
                value={formData.emailAddress}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="sourceLanguage">Source Language</label>
                <select
                  id="sourceLanguage"
                  name="sourceLanguage"
                  value={formData.sourceLanguage}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select source language</option>

                  {sourceLanguageOptions.map((language) => (
                    <option key={language} value={language}>
                      {language}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="targetLanguage">Target Language</label>
                <input
                  id="targetLanguage"
                  type="text"
                  name="targetLanguage"
                  value={fixedTargetLanguage}
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-field">
                <label htmlFor="translationType">Translation Type</label>
                <select
                  id="translationType"
                  name="translationType"
                  value={formData.translationType}
                  onChange={handleChange}
                  required
                >
                  <option value="Certified">Certified Translation</option>
                  <option value="Standard">Standard Translation</option>
                </select>
              </div>

              <div className="form-field">
                <label htmlFor="deadline">Deadline</label>
                <select
                  id="deadline"
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                  required
                >
                  <option value="Standard">Standard</option>
                  <option value="Urgent">Urgent</option>
                  <option value="Express">Express</option>
                </select>
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="message">Message</label>
              <textarea
                id="message"
                name="message"
                placeholder="Please describe the document and the service you need"
                rows="5"
                value={formData.message}
                onChange={handleChange}
                required
              ></textarea>
            </div>

            <div
              className={`file-upload-group quote-file-dropzone ${isDraggingFiles ? "drag-active" : ""
                }`}
              onDragOver={handleDocumentsDragOver}
              onDragEnter={handleDocumentsDragOver}
              onDragLeave={handleDocumentsDragLeave}
              onDrop={handleDocumentsDrop}
            >
              <label htmlFor="documentFiles" className="file-upload-label">
                Attach documents
              </label>

              <input
                ref={fileInputRef}
                id="documentFiles"
                type="file"
                name="documentFiles"
                onChange={handleChange}
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                multiple
                className="quote-file-input"
              />

              <label htmlFor="documentFiles" className="quote-file-dropzone-label">
                <strong>Choose files</strong>
                <span>or drag and drop documents here</span>
              </label>

              <small className="file-help-text">
                Accepted: PDF, JPG, PNG, DOCX. Maximum 10 files, 10MB per file.
              </small>

              {documentFiles.length > 0 && (
                <div className="selected-files">
                  {documentFiles.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="selected-file-item">
                      <span className="selected-file-name" title={file.name}>
                        {file.name}
                      </span>

                      <button
                        type="button"
                        className="remove-file-btn"
                        onClick={() => handleRemoveFile(index)}
                        aria-label={`Remove ${file.name}`}
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {usesArabic && (
              <div
                className={`file-upload-group names-upload-group quote-file-dropzone ${isDraggingNamesFile ? "drag-active" : ""
                  }`}
                onDragOver={handleNamesDragOver}
                onDragEnter={handleNamesDragOver}
                onDragLeave={handleNamesDragLeave}
                onDrop={handleNamesDrop}
              >
                <label htmlFor="namesFile" className="file-upload-label">
                  Attach names list
                </label>

                <input
                  ref={namesFileInputRef}
                  id="namesFile"
                  type="file"
                  name="namesFile"
                  onChange={handleChange}
                  accept=".pdf,.jpg,.jpeg,.png,.docx"
                  multiple
                  className="quote-file-input"
                />

                <label htmlFor="namesFile" className="quote-file-dropzone-label">
                  <strong>Choose file</strong>
                  <span>or drag and drop the names list here</span>
                </label>

                <small className="file-help-text">
                  Optional: upload a file with the correct spelling of names when Arabic is one of the languages.
                </small>

                {namesFiles.length > 0 && (
                  <div className="selected-files">
                    {namesFiles.map((file, index) => (
                      <div key={`${file.name}-${index}`} className="selected-file-item">
                        <span className="selected-file-name" title={file.name}>
                          {file.name}
                        </span>

                        <button
                          type="button"
                          className="remove-file-btn"
                          onClick={() => handleRemoveNamesFile(index)}
                          aria-label={`Remove ${file.name}`}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={isSending}>
              {isSending ? "Sending..." : "Send Enquiry"}
            </button>

            {statusMessage && (
              <p className={`form-status ${isError ? "error-message" : "success-message"}`}>
                {statusMessage}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}